<template>
    <article class="article">
        <AppBreadcrumbs
            :base-url="runtimeConfig.public.BASE_URL"
            :parents="[
                {
                    name: config.brand_name,
                    full_slug: '',
                },
                ...parentPages,
            ]"
            :current="{
                name: article.name,
                full_slug: article.full_slug,
            }"
        />

        <div class="article__main">
            <div class="article__content">
                <ArticleHead
                    :title="renderHighlightedText(article.content.title)"
                    :subtitle="article.content.subtitle"
                />

                <ArticleMetadata
                    v-if="author?.content"
                    :avatar="author.content.image.filename"
                    :avatar-alt="author.content.image.alt"
                    :full-name="`${author.content.first_name} ${author.content.last_name}`"
                    :slug="author.full_slug"
                    :date="article.published_at"
                />

                <ArticleBanner
                    :src="article.content.image.filename"
                    :alt="article.content.image.alt"
                />

                <!-- Deliberately no `priority` forwarding here: ArticleBanner
                     above is this page type's sole LCP candidate (eager +
                     fetchpriority + preload), so a hero/main-header even in
                     first position must stay lazy — two priority images would
                     fight each other on every article that opens with one. -->
                <StoryblokComponent
                    v-for="contentBlok in article.content.content"
                    :key="contentBlok._uid"
                    :story="article"
                    :blok="contentBlok"
                />

                <div
                    v-if="article.content.has_affiliate_links"
                    class="article__disclaimer"
                    v-html="renderRichText(config.affiliate_disclaimer)"
                />

                <PreferredSourceCta
                    :publication-name="config.brand_name"
                    :publication-url="runtimeConfig.public.BASE_URL"
                    @view="capture('preferred_source_cta_viewed')"
                    @click="capture('preferred_source_clicked')"
                    @fallback-click="capture('preferred_source_fallback_clicked')"
                    @error="stage => capture('preferred_source_failed', { stage })"
                />
            </div>

            <div class="article__sidebar">
                <StoryblokComponent
                    v-for="contentBlok in article.content.sidebar"
                    :key="contentBlok._uid"
                    :story="article"
                    :blok="contentBlok"
                />
            </div>
        </div>
        <StaticFeaturedArticles
            title="Explore <span class='u-highlight'>our</span> guides!"
            text="Made by locals, to help you make the most of your trip."
            :articles="recommendedArticles"
        />

        <ArticleCtaBar
            v-if="article.content.cta_url"
            :title="article.content.cta_title"
            :text="article.content.cta_text"
            :link="ctaUrlWithSlug"
            :button="article.content.cta_button"
            @show="capture('cta_bar_shown', { title: article.content.cta_title })"
            @dismiss="capture('cta_bar_dismissed', { title: article.content.cta_title })"
            @click="capture('affiliate_click', { placement: 'cta-bar', title: article.content.cta_title, url: ctaUrlWithSlug })"
        />
    </article>
</template>

<script setup>
import ArticleBanner from '~/components/article-banner/article-banner.vue'
import ArticleCtaBar from '~/components/article-cta-bar/article-cta-bar.vue'
import ArticleHead from '~/components/article-head/article-head.vue'
import ArticleMetadata from '~/components/article-metadata/article-metadata.vue'
import StaticFeaturedArticles from '~/components/featured-articles/static-featured-articles.vue'
import PreferredSourceCta from '~/components/preferred-source-cta/preferred-source-cta.vue'
import renderHighlightedText from '~/helpers/renderHighlightedText'
import seoTitle from '~/helpers/seoTitle'

const { data } = defineProps({
    data: {
        type: Object,
        required: true,
    },
})
const route = useRoute()
const { slug } = route.params
const { capture } = useTracking()

const { data: config } = await useConfig()

const parentPages = computed(() => {
    if (Array.isArray(slug) && slug.length > 1) {
        return slug.slice(0, -1).map((item, index) => {
            return {
                name: (item.charAt(0).toUpperCase() + item.slice(1)).replace('-', ' '),
                full_slug: `${slug.slice(0, index + 1).join('/')}`,
            }
        })
    }

    return []
})

const { author, article, recommendedArticles } = data

const ctaUrlWithSlug = computed(() => article.content.cta_url.replace('{slug}', `cta-${route.path.split('/').filter(Boolean).join('-')}`))

const runtimeConfig = useRuntimeConfig()

const title = seoTitle(article)
const authorName = author?.content
    ? `${author.content.first_name} ${author.content.last_name}`
    : null

const structuredData = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': renderRichText(article.content.title).replaceAll(/<[^>]*>?/g, ''),
    'image': [
        `${article.content.image.filename}/m/960x960`,
        `${article.content.image.filename}/m/1920x1080`,
        `${article.content.image.filename}/m/960x720`,
    ],
    'datePublished': article.first_published_at,
    'dateModified': article.published_at,
    ...(authorName
        ? {
                author: [{
                    '@type': 'Person',
                    'name': authorName,
                    'url': `${runtimeConfig.public.BASE_URL}/authors/${author.slug}`,
                }],
            }
        : {}),
})

onMounted(() => {
    capture('article_viewed', {
        title,
        author: authorName,
        has_affiliate_links: !!article.content.has_affiliate_links,
    })
})

// Only ever opt *out*. `index, follow` is already the @nuxtjs/robots default, and setting
// it explicitly would override the site-wide noindex that preview/staging hosts get from
// server/plugins/site-indexable.js. `noindex, follow` and not `useRobotsRule(false)`:
// `false` resolves to the module's `robotsDisabledValue` (`noindex, nofollow`), whose
// nofollow also cuts the crawl path to any indexable children.
if (article.content.index === false)
    useRobotsRule('noindex, follow')

useHead({
    title,
    meta: [
        {
            hid: 'description',
            name: 'description',
            content: article.content.meta_description,
        },
        {
            property: 'og:type',
            content: 'article',
        },
        {
            property: 'og:url',
            content: `${runtimeConfig.public.BASE_URL}/${article.full_slug}`,
        },
        {
            property: 'og:title',
            content: title,
        },
        {
            property: 'og:description',
            content: article.content.meta_description,
        },
        {
            property: 'og:image',
            content: `${article.content.image.filename}/m/1200x630`,
        },
        {
            hid: 'twitter:card',
            name: 'twitter:card',
            content: 'summary_large_image',
        },
        {
            hid: 'twitter:domain',
            name: 'twitter:domain',
            content: runtimeConfig.public.BASE_URL,
        },
        {
            hid: 'twitter:url',
            name: 'twitter:url',
            content: `${runtimeConfig.public.BASE_URL}/${article.full_slug}`,
        },
        {
            hid: 'twitter:title',
            name: 'twitter:title',
            content: title,
        },
        {
            hid: 'twitter:description',
            name: 'twitter:description',
            content: article.content.meta_description,
        },
        {
            hid: 'twitter:image',
            name: 'twitter:image',
            content: `${article.content.image.filename}/m/1200x630`,
        },
    ],
    script: [
        {
            hid: 'structured-data',
            type: 'application/ld+json',
            innerHTML: structuredData,
        },
    ],
})
</script>
